import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { AppShell, PageHeader } from '@/components/AppShell';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { PanelLeftOpen, Plus, Pencil, Trash2, Copy, FileText, UserRound, Loader2 } from 'lucide-react';
import {
  listTemplates,
  listPersonas,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  duplicateTemplate,
  createPersona,
  updatePersona,
  deletePersona,
  type Persona,
  type PromptTemplate,
  type CreateTemplatePayload,
  type AccountOverride,
} from '@/services/prompts-api';
import { listBrands, type Brand } from '@/services/brands-api';
import { fetchDevices, type VMOSDevice } from '@/services/vmos-api';
import { AccountPicker } from '@/components/prompts/AccountPicker';
import { PLATFORM_OPTIONS } from '@/components/prompts/SavedPromptsPanel';

const DEFAULT_LLM_MODEL = 'gemini-3-flash-preview';
const DEFAULT_COMMENT_MODEL = 'gemini-3-flash-preview';

type TabId = 'templates' | 'personas';

export default function PromptsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabId>('templates');

  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState<PromptTemplate | null>(null);

  const [editingPersona, setEditingPersona] = useState<Persona | null>(null);
  const [creatingPersona, setCreatingPersona] = useState(false);
  const [confirmDeletePersona, setConfirmDeletePersona] = useState<Persona | null>(null);
  const [accountFilter, setAccountFilter] = useState<string>('all');

  const { data: templates = [], isLoading: loadingTemplates } = useQuery({
    queryKey: ['prompt-templates'],
    queryFn: () => listTemplates(),
  });
  const { data: personas = [], isLoading: loadingPersonas } = useQuery({
    queryKey: ['personas'],
    queryFn: () => listPersonas(),
  });
  const { data: brands = [] } = useQuery({
    queryKey: ['brands'],
    queryFn: listBrands,
  });
  const { data: devicesData } = useQuery({
    queryKey: ['vmos-devices'],
    queryFn: fetchDevices,
    staleTime: 60_000,
  });
  const devices = devicesData?.devices ?? [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['prompt-templates'] });
    qc.invalidateQueries({ queryKey: ['personas'] });
  };

  const createTemplateMut = useMutation({
    mutationFn: (p: CreateTemplatePayload) => createTemplate(p),
    onSuccess: () => {
      invalidate();
      toast.success('Prompt created');
      setCreatingTemplate(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const updateTemplateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: CreateTemplatePayload }) => updateTemplate(id, p),
    onSuccess: () => {
      invalidate();
      toast.success('Prompt updated');
      setEditingTemplate(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteTemplateMut = useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => {
      invalidate();
      toast.success('Prompt deleted');
      setConfirmDeleteTemplate(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const duplicateTemplateMut = useMutation({
    mutationFn: (id: string) => duplicateTemplate(id),
    onSuccess: () => {
      invalidate();
      toast.success('Prompt duplicated');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createPersonaMut = useMutation({
    mutationFn: (p: { name: string; persona_text: string; account_pad_ids?: string[] }) => createPersona(p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['personas'] });
      toast.success('Persona created');
      setCreatingPersona(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const updatePersonaMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: { name?: string; persona_text?: string; account_pad_ids?: string[] } }) => updatePersona(id, p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['personas'] });
      toast.success('Persona updated');
      setEditingPersona(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const deletePersonaMut = useMutation({
    mutationFn: (id: string) => deletePersona(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['personas'] });
      toast.success(
        data.orphaned_templates > 0
          ? `Persona deleted. ${data.orphaned_templates} prompt(s) kept their saved persona snapshot but lost the live link.`
          : 'Persona deleted',
      );
      setConfirmDeletePersona(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sortedTemplates = useMemo(
    () => templates
      .filter((t) => {
        const padIds = (t.account_overrides ?? []).map((o) => o.pad_id);
        return accountFilter === 'all' || padIds.length === 0 || padIds.includes(accountFilter);
      })
      .sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [templates, accountFilter],
  );
  const sortedPersonas = useMemo(
    () => personas
      .filter((p) => {
        const padIds = p.account_pad_ids ?? [];
        return accountFilter === 'all' || padIds.length === 0 || padIds.includes(accountFilter);
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
    [personas, accountFilter],
  );

  const isLoading = tab === 'templates' ? loadingTemplates : loadingPersonas;
  const listEmpty = tab === 'templates' ? sortedTemplates.length === 0 : sortedPersonas.length === 0;

  return (
    <AppShell>
      <div className="flex flex-col h-full">
        <PageHeader>
            <SidebarTrigger>
              <PanelLeftOpen className="h-4 w-4" />
            </SidebarTrigger>
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold leading-tight">Prompts</h1>
              <p className="text-xs text-muted-foreground">
                Saved engagement workflows and personas you can reuse on a run or schedule.
              </p>
            </div>
            <div className="ml-auto">
              {tab === 'templates' ? (
                <Button size="sm" onClick={() => setCreatingTemplate(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New prompt
                </Button>
              ) : (
                <Button size="sm" onClick={() => setCreatingPersona(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New persona
                </Button>
              )}
            </div>
        </PageHeader>

          <div className="px-6 py-4 border-b flex items-center gap-3">
            <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
              <TabsList>
                <TabsTrigger value="templates">Prompt templates</TabsTrigger>
                <TabsTrigger value="personas">Personas</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Account</span>
              <Select value={accountFilter} onValueChange={setAccountFilter}>
                <SelectTrigger className="h-8 w-[220px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All accounts</SelectItem>
                  {devices.map((d) => (
                    <SelectItem key={d.pad_id} value={d.pad_id}>
                      {d.device_name}
                      {d.platform ? ` · ${d.platform}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex-1 overflow-auto px-6 py-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading…
              </div>
            ) : listEmpty ? (
              tab === 'templates' ? (
                <EmptyState
                  icon={<FileText className="h-8 w-8 text-muted-foreground/40" />}
                  title="No saved prompts yet"
                  body="Save a prompt from the Runs page (or the Schedule sheet) to capture a full engagement
                        workflow — task, persona, brand, models, and limits — so you can re-run or schedule it
                        without pasting it every time."
                />
              ) : (
                <EmptyState
                  icon={<UserRound className="h-8 w-8 text-muted-foreground/40" />}
                  title="No personas yet"
                  body="Save a persona from the Runs page (or the Schedule sheet) to reuse the 'who these agents
                        are / how they talk' block across platforms and attach it to saved prompts."
                />
              )
            ) : tab === 'templates' ? (
              <div className="border rounded-lg divide-y">
                {sortedTemplates.map((t) => (
                  <div key={t.id} className="flex items-start justify-between gap-4 px-4 py-4 hover:bg-muted/30 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-3 mb-1">
                        <h2 className="text-sm font-semibold truncate">{t.name}</h2>
                        {t.platform && (
                          <span className="text-[10px] uppercase tracking-wider text-primary border rounded px-1.5 py-0.5">
                            {t.platform}
                          </span>
                        )}
                        {t.usage_count > 0 && (
                          <span className="text-xs font-mono text-muted-foreground">
                            used {t.usage_count}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-1.5 text-xs text-muted-foreground">
                        {t.persona_name && <span className="rounded bg-muted px-1.5 py-0.5">Persona: {t.persona_name}</span>}
                        {t.brand_name && <span className="rounded bg-muted px-1.5 py-0.5">Brand: {t.brand_name}</span>}
                        <span className="rounded bg-muted px-1.5 py-0.5">Max steps {t.max_steps}</span>
                        {t.accounts.length > 0 ? (
                          t.accounts.map((a) => (
                            <span key={a.pad_id} className="rounded bg-muted px-1.5 py-0.5">
                              Acct: {a.device_name || a.pad_id}
                            </span>
                          ))
                        ) : (
                          <span className="rounded bg-muted px-1.5 py-0.5">Any account</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 font-mono">{t.task}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => duplicateTemplateMut.mutate(t.id)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingTemplate(t)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteTemplate(t)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border rounded-lg divide-y">
                {sortedPersonas.map((p) => (
                  <div key={p.id} className="flex items-start justify-between gap-4 px-4 py-4 hover:bg-muted/30 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-3 mb-1.5">
                        <h2 className="text-sm font-semibold truncate">{p.name}</h2>
                        {p.usage_count > 0 && (
                          <span className="text-xs font-mono text-muted-foreground">
                            used {p.usage_count}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-1.5 text-xs text-muted-foreground">
                        {p.accounts.length > 0 ? (
                          p.accounts.map((a) => (
                            <span key={a.pad_id} className="rounded bg-muted px-1.5 py-0.5">
                              Acct: {a.device_name || a.pad_id}
                            </span>
                          ))
                        ) : (
                          <span className="rounded bg-muted px-1.5 py-0.5">Any account</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-3 font-mono">{p.persona_text}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => setEditingPersona(p)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDeletePersona(p)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      {/* Template dialogs */}
      {(creatingTemplate || editingTemplate) && (
        <TemplateFormDialog
          key={editingTemplate?.id ?? 'new'}
          title={editingTemplate ? `Edit ${editingTemplate.name}` : 'New prompt'}
          submitLabel={editingTemplate ? 'Save changes' : 'Create'}
          initial={editingTemplate ?? undefined}
          personas={personas}
          brands={brands}
          devices={devices}
          isSubmitting={createTemplateMut.isPending || updateTemplateMut.isPending}
          onClose={() => {
            setCreatingTemplate(false);
            setEditingTemplate(null);
          }}
          onSubmit={(payload) => {
            if (editingTemplate) {
              updateTemplateMut.mutate({ id: editingTemplate.id, p: payload });
            } else {
              createTemplateMut.mutate(payload);
            }
          }}
        />
      )}

      {/* Persona dialogs */}
      {(creatingPersona || editingPersona) && (
        <PersonaFormDialog
          key={editingPersona?.id ?? 'new'}
          title={editingPersona ? `Edit ${editingPersona.name}` : 'New persona'}
          submitLabel={editingPersona ? 'Save changes' : 'Create'}
          initial={editingPersona ?? undefined}
          isSubmitting={createPersonaMut.isPending || updatePersonaMut.isPending}
          onClose={() => {
            setCreatingPersona(false);
            setEditingPersona(null);
          }}
          onSubmit={(name, persona_text, accountPadIds) => {
            if (editingPersona) {
              updatePersonaMut.mutate({ id: editingPersona.id, p: { name, persona_text, account_pad_ids: accountPadIds } });
            } else {
              createPersonaMut.mutate({ name, persona_text, account_pad_ids: accountPadIds });
            }
          }}
        />
      )}

      <AlertDialog open={!!confirmDeleteTemplate} onOpenChange={(o) => !o && setConfirmDeleteTemplate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDeleteTemplate?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Saved runs/schedules that already launched are unaffected. This only removes the reusable prompt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTemplateMut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteTemplateMut.isPending}
              onClick={() => confirmDeleteTemplate && deleteTemplateMut.mutate(confirmDeleteTemplate.id)}
            >
              {deleteTemplateMut.isPending ? 'Deleting…' : 'Delete prompt'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDeletePersona} onOpenChange={(o) => !o && setConfirmDeletePersona(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDeletePersona?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Prompts that reference this persona keep their saved persona text snapshot but lose the live link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePersonaMut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deletePersonaMut.isPending}
              onClick={() => confirmDeletePersona && deletePersonaMut.mutate(confirmDeletePersona.id)}
            >
              {deletePersonaMut.isPending ? 'Deleting…' : 'Delete persona'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

function EmptyState({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-20 text-center">
      {icon}
      <div className="text-sm font-medium">{title}</div>
      <p className="text-xs text-muted-foreground max-w-md">{body}</p>
    </div>
  );
}

interface TemplateFormState {
  name: string;
  platform: string; // 'none' or a platform
  task: string;
  personaId: string; // 'none' or persona id
  brandId: string; // 'none' or brand id
  llmModel: string;
  commentLlmModel: string;
  maxSteps: number;
  disableProactiveSearch: boolean;
  captureRequired: boolean;
  maxCommentsPerSession: string;
  maxSearchesPerSession: string;
  accountOverrides: AccountOverride[];
}

function TemplateFormDialog({
  title,
  submitLabel,
  initial,
  personas,
  brands,
  devices,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  title: string;
  submitLabel: string;
  initial?: PromptTemplate;
  personas: Persona[];
  brands: Brand[];
  devices: VMOSDevice[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (p: CreateTemplatePayload) => void;
}) {
  const [state, setState] = useState<TemplateFormState>({
    name: initial?.name ?? '',
    platform: initial?.platform ?? 'none',
    task: initial?.task ?? '',
    personaId: initial?.persona_id ?? 'none',
    brandId: initial?.brand_id ?? 'none',
    llmModel: DEFAULT_LLM_MODEL,
    commentLlmModel: DEFAULT_COMMENT_MODEL,
    maxSteps: initial?.max_steps ?? 200,
    disableProactiveSearch: initial?.disable_proactive_search ?? false,
    captureRequired: initial?.capture_required ?? true,
    maxCommentsPerSession: initial?.max_comments_per_session != null ? String(initial.max_comments_per_session) : '',
    maxSearchesPerSession: initial?.max_searches_per_session != null ? String(initial.max_searches_per_session) : '',
    accountOverrides: initial?.account_overrides ?? [],
  });

  const persona = personas.find((p) => p.id === state.personaId);
  const valid = state.name.trim().length > 0 && state.task.trim().length > 0;
  const deviceName = (padId: string) =>
    devices.find((d) => d.pad_id === padId)?.device_name || padId;

  const setAccounts = (padIds: string[]) => {
    setState((s) => {
      const existing = new Map(s.accountOverrides.map((o) => [o.pad_id, o]));
      return { ...s, accountOverrides: padIds.map((pid) => existing.get(pid) ?? { pad_id: pid }) };
    });
  };
  const updateOverride = (padId: string, patch: Partial<AccountOverride>) => {
    setState((s) => ({
      ...s,
      accountOverrides: s.accountOverrides.map((o) => (o.pad_id === padId ? { ...o, ...patch } : o)),
    }));
  };

  const handleSubmit = () => {
    onSubmit({
      name: state.name.trim(),
      platform: state.platform === 'none' ? null : state.platform,
      task: state.task.trim(),
      persona_id: state.personaId === 'none' ? null : state.personaId,
      persona_text: persona ? persona.persona_text : null,
      brand_id: state.brandId === 'none' ? null : state.brandId,
      llm_model: DEFAULT_LLM_MODEL,
      comment_llm_model: DEFAULT_COMMENT_MODEL,
      max_steps: state.maxSteps,
      disable_proactive_search: state.disableProactiveSearch,
      capture_required: state.captureRequired,
      max_comments_per_session: state.maxCommentsPerSession === '' ? null : Number(state.maxCommentsPerSession),
      max_searches_per_session: state.maxSearchesPerSession === '' ? null : Number(state.maxSearchesPerSession),
      account_overrides: state.accountOverrides,
    });
  };

  const set = (patch: Partial<TemplateFormState>) => setState((s) => ({ ...s, ...patch }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Captures the task + persona + brand + settings. Load it on the Runs page or Schedule sheet to launch
            the whole engagement workflow in one click.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="t-name">Name</Label>
              <Input id="t-name" value={state.name} onChange={(e) => set({ name: e.target.value })} placeholder="Grove — full engagement on FB Groups" />
            </div>
            <div className="space-y-1.5">
              <Label>Platform (optional tag)</Label>
              <Select value={state.platform} onValueChange={(v) => set({ platform: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any / unspecified</SelectItem>
                  {PLATFORM_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="t-task">Task prompt</Label>
            <Textarea
              id="t-task"
              value={state.task}
              onChange={(e) => set({ task: e.target.value })}
              rows={5}
              className="font-mono text-xs"
              placeholder="Full engagement on Facebook Groups — open the Groups tab, react/comment…"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Persona</Label>
              <Select value={state.personaId} onValueChange={(v) => set({ personaId: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {personas.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {persona ? (
                <p className="text-[11px] text-muted-foreground line-clamp-2">{persona.persona_text}</p>
              ) : (
                <p className="text-[11px] text-muted-foreground">Saves a snapshot of the persona text on the prompt.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Brand</Label>
              <Select value={state.brandId} onValueChange={(v) => set({ brandId: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {brands.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="t-maxsteps">Max steps</Label>
              <Input
                id="t-maxsteps"
                type="number"
                min={10}
                max={2000}
                value={state.maxSteps}
                onChange={(e) => set({ maxSteps: Math.max(10, Number(e.target.value) || 10) })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-maxc">Max comments / session</Label>
              <Input
                id="t-maxc"
                type="number"
                min={0}
                value={state.maxCommentsPerSession}
                placeholder="Auto (8)"
                onChange={(e) => set({ maxCommentsPerSession: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-maxs">Max searches / session</Label>
              <Input
                id="t-maxs"
                type="number"
                min={0}
                value={state.maxSearchesPerSession}
                placeholder="Auto (5)"
                onChange={(e) => set({ maxSearchesPerSession: e.target.value })}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-2">
              <Switch
                id="t-dps"
                checked={state.disableProactiveSearch}
                onCheckedChange={(v) => set({ disableProactiveSearch: v })}
              />
              <Label htmlFor="t-dps" className="text-xs text-muted-foreground">Feed only (no proactive search)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="t-cap"
                checked={state.captureRequired}
                onCheckedChange={(v) => set({ captureRequired: v })}
              />
              <Label htmlFor="t-cap" className="text-xs text-muted-foreground">Require post-URL capture for FB posts / Reddit</Label>
            </div>
          </div>

          {/* Accounts + per-account overrides */}
          <div className="space-y-2 border-t pt-4">
            <Label>Accounts (optional)</Label>
            <AccountPicker
              value={state.accountOverrides.map((o) => o.pad_id)}
              onChange={setAccounts}
            />
            <p className="text-[11px] text-muted-foreground">
              Leave empty for any account. Add an account to scope this prompt and optionally
              override its persona and task.
            </p>

            {state.accountOverrides.map((o) => (
              <div key={o.pad_id} className="border rounded-lg p-3 space-y-2 bg-muted/20">
                <div className="text-xs font-medium">{deviceName(o.pad_id)}</div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Persona override (optional)</Label>
                  <Select
                    value={o.persona_id ?? 'none'}
                    onValueChange={(v) => updateOverride(o.pad_id, { persona_id: v === 'none' ? null : v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Use base persona</SelectItem>
                      {personas.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="…or inline persona text (overrides the pick above)"
                    value={o.persona_text ?? ''}
                    onChange={(e) => updateOverride(o.pad_id, { persona_text: e.target.value })}
                    rows={2}
                    className="font-mono text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Task override (replaces base task)</Label>
                  <Textarea
                    placeholder="Leave blank to use the base task"
                    value={o.task ?? ''}
                    onChange={(e) => updateOverride(o.pad_id, { task: e.target.value })}
                    rows={2}
                    className="font-mono text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Task append (added to base task)</Label>
                  <Textarea
                    placeholder="Extra instructions for this account"
                    value={o.task_append ?? ''}
                    onChange={(e) => updateOverride(o.pad_id, { task_append: e.target.value })}
                    rows={2}
                    className="font-mono text-xs"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || isSubmitting}>
            {isSubmitting ? 'Saving…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PersonaFormDialog({
  title,
  submitLabel,
  initial,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  title: string;
  submitLabel: string;
  initial?: Persona;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (name: string, persona_text: string, accountPadIds: string[]) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [text, setText] = useState(initial?.persona_text ?? '');
  const [accountPadIds, setAccountPadIds] = useState<string[]>(initial?.account_pad_ids ?? []);
  const valid = name.trim().length > 0 && text.trim().length > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            A persona is the reusable "who these agents are / how they talk / their interests" block.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="p-name">Name</Label>
            <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Grove commenter — casual expert" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-text">Persona text</Label>
            <Textarea
              id="p-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              className="font-mono text-xs"
              placeholder="22 year old college student who loves cooking and gym content, types casually with lowercase and occasional emojis…"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Accounts (optional)</Label>
            <AccountPicker value={accountPadIds} onChange={setAccountPadIds} />
            <p className="text-[11px] text-muted-foreground">
              Leave empty for any account. Scoping is for organization/filtering.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(name.trim(), text.trim(), accountPadIds)} disabled={!valid || isSubmitting}>
            {isSubmitting ? 'Saving…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
